import React, { type FunctionComponent, useState } from 'react'
import { CardBody, spacing } from '@retailer-platform/shared-components'
import { Checkbox } from '@retailer-platform/shared-components/src/tds'
import styled from '@emotion/styled'
import { TimePickerField } from './TimePickerField'
import { CronExpressionDisplay } from './CronExpressionDisplay'
import { convertWeeklyToCron } from './cronUtils'

const StyledCardBody = styled(CardBody)({
  padding: '12px',
})

const Section = styled.div({
  marginBottom: spacing.X16,
})

const DaysGrid = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: spacing.X16,
  marginTop: spacing.X8,
})

interface Props {
  onScheduleChange?: (schedule: { days: number[]; startTime: string }) => void
  defaultSchedule: { days: number[]; startTime: string }
}

const DAYS_OF_WEEK = [
  { id: 0, label: 'Sunday' },
  { id: 1, label: 'Monday' },
  { id: 2, label: 'Tuesday' },
  { id: 3, label: 'Wednesday' },
  { id: 4, label: 'Thursday' },
  { id: 5, label: 'Friday' },
  { id: 6, label: 'Saturday' },
]

export const WeeklySchedule: FunctionComponent<React.PropsWithChildren<Props>> = ({
  onScheduleChange,
  defaultSchedule,
}) => {
  const [selectedDays, setSelectedDays] = useState<number[]>([...defaultSchedule.days])
  const [startTime, setStartTime] = useState<string>(defaultSchedule.startTime)
  const { cron, readable } = convertWeeklyToCron({ days: selectedDays, startTime })

  const handleDayToggle = (day: number) => {
    const newSelectedDays = selectedDays.includes(day)
      ? selectedDays.filter(d => d !== day)
      : [...selectedDays, day]
    setSelectedDays(newSelectedDays)
    onScheduleChange?.({
      days: newSelectedDays,
      startTime,
    })
  }

  return (
    <StyledCardBody>
      <Section>
        <div>Select days of the week:</div>
        <DaysGrid>
          {DAYS_OF_WEEK.map(day => (
            <Checkbox
              key={day.id}
              id={`day-${day.id}`}
              checked={selectedDays.includes(day.id)}
              onChange={() => handleDayToggle(day.id)}
              label={day.label}
            />
          ))}
        </DaysGrid>
      </Section>

      <TimePickerField
        value={startTime}
        onChange={newTime => {
          setStartTime(newTime)
          onScheduleChange?.({
            days: selectedDays,
            startTime: newTime,
          })
        }}
      />

      <CronExpressionDisplay cronExpression={cron} cronDescription={readable} />
    </StyledCardBody>
  )
}
